import { useState } from "react";
import {
  Users, CheckCircle, Award, Copy, RefreshCw, BookOpen,
  ClipboardList, Trophy, Search, MessageSquare, Trash2, Plus, Eye,
} from "lucide-react";
import SporterProfielModal from "./SporterProfielModal";

// ─── Helpers ─────────────────────────────────────────────────────────────────
function MiniProgress({ done, total, color = "#C9A84C" }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 bg-[#1c1c1c] rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${(done / total) * 100}%`, background: color }} />
      </div>
      <span className="text-zinc-400 text-xs">{done}/{total}</span>
    </div>
  );
}

const DOEL_BADGE = {
  vetverlies:  "text-orange-400 bg-orange-500/15 border-orange-500/25",
  spieropbouw: "text-blue-400   bg-blue-500/15   border-blue-500/25",
  onderhoud:   "text-green-400  bg-green-500/15  border-green-500/25",
  powerlift:   "text-purple-400 bg-purple-500/15 border-purple-500/25",
  hyrox:       "text-cyan-400   bg-cyan-500/15   border-cyan-500/25",
};

function SchemaBadge({ schema }) {
  const doel = schema.product?.split("-")[0] || "vetverlies";
  return (
    <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full border ${DOEL_BADGE[doel] || DOEL_BADGE.vetverlies}`}>
      {schema.label}
    </span>
  );
}

// ─── CursistenTab ─────────────────────────────────────────────────────────────
export default function CursistenTab({
  users, stats, loading, api, token,
  unreadCounts,
  onUserDeleted, onUserUpdated,
  onSchemaPanel, onBericht, onShowCreate,
  showToast, onGoToTrainModus,
}) {
  const [search,              setSearch]             = useState("");
  const [deleteConfirm,       setDeleteConfirm]      = useState(null);
  const [sbToggleLoading,     setSbToggleLoading]    = useState(null);
  const [vtToggleLoading,     setVtToggleLoading]    = useState(null);
  const [rtToggleLoading,     setRtToggleLoading]    = useState(null);
  const [resendLoading,       setResendLoading]      = useState(null);
  const [welcomeMailLoading,  setWelcomeMailLoading] = useState(null);
  const [profielCursist,      setProfielCursist]     = useState(null);

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async (id) => {
    try {
      await api.deleteUser(id);
      onUserDeleted(id);
      setDeleteConfirm(null);
      showToast("Cursist verwijderd");
    } catch { showToast("Verwijderen mislukt", "error"); }
  };

  const handleResend = async (id) => {
    setResendLoading(id);
    try {
      const r = await api.resendCode(id);
      showToast(r.email_sent ? "E-mail verstuurd!" : `Code: ${r.access_code} (e-mail niet verstuurd)`);
    } catch { showToast("Mislukt", "error"); }
    setResendLoading(null);
  };

  const handleResendWelcome = async (id) => {
    setWelcomeMailLoading(id);
    try {
      const r = await api.resendWelcomeMail(id);
      showToast(`Welkomstmail verstuurd naar ${r.email}!`);
    } catch (e) { showToast(e.message || "Welkomstmail versturen mislukt", "error"); }
    setWelcomeMailLoading(null);
  };

  const handlePlanToggle = async (id, currentPlan) => {
    const newPlan = currentPlan === "schema" ? "full" : "schema";
    try {
      await api.updatePlan(id, newPlan);
      onUserUpdated({ id, plan: newPlan });
      showToast(`Plan gewijzigd naar ${newPlan === "full" ? "Cursus" : "Schema"}`);
    } catch { showToast("Plan wijzigen mislukt", "error"); }
  };

  const handleSchemaBuilderToggle = async (id, current) => {
    const newVal = !current;
    setSbToggleLoading(id);
    try {
      await api.toggleSchemaBuilder(id, newVal);
      onUserUpdated({
        id,
        schema_builder_unlocked: newVal,
        schema_builder_unlock_type: newVal ? "handmatig" : null,
      });
      showToast(newVal ? "Schema Maker toegang gegeven!" : "Schema Maker toegang ingetrokken");
    } catch { showToast("Wijzigen mislukt", "error"); }
    setSbToggleLoading(null);
  };

  const handleVrijeTrainingToggle = async (id, current) => {
    const newVal = !current;
    setVtToggleLoading(id);
    try {
      await api.toggleVrijeTraining(id, newVal);
      onUserUpdated({ id, vrije_training_enabled: newVal });
      showToast(newVal ? "Vrije Training toegang gegeven!" : "Vrije Training toegang ingetrokken");
    } catch { showToast("Wijzigen mislukt", "error"); }
    setVtToggleLoading(null);
  };

  const handleRoosterToggle = async (id, current) => {
    const newVal = !current;
    setRtToggleLoading(id);
    try {
      await api.toggleRooster(id, newVal);
      onUserUpdated({ id, persoonlijk_rooster_enabled: newVal });
      showToast(newVal ? "Rooster toegang gegeven!" : "Rooster toegang ingetrokken");
    } catch { showToast("Wijzigen mislukt", "error"); }
    setRtToggleLoading(null);
  };

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
        {[
          { icon: <Users size={18} />,       label: "Totaal cursisten",  val: stats.total        ?? "–" },
          { icon: <CheckCircle size={18} />, label: "Actieve accounts",  val: stats.active       ?? "–" },
          { icon: <Award size={18} />,       label: "Certificaten",      val: stats.certificates ?? "–" },
        ].map(({ icon, label, val }) => (
          <div key={label} className="course-stat-card">
            <div className="text-[#C9A84C] mb-2">{icon}</div>
            <div className="text-2xl font-bold text-white">{val}</div>
            <div className="text-zinc-500 text-xs mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Zoekbalk */}
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Zoeken op naam of e-mail..."
          className="course-input pl-9 max-w-sm text-sm" />
      </div>

      {/* Tabel */}
      <div className="course-card p-0 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-zinc-500">Laden...</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Users size={32} className="text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">Nog geen cursisten.{" "}
              <button onClick={onShowCreate} className="text-[#C9A84C] hover:underline">Maak de eerste aan.</button>
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="border-b border-white/5">
                  {["Naam / E-mail", "Toegangscode", "Schema's", "Aangemaakt", "Schema Maker", "Vrije Training", "Rooster", "Voortgang", "Acties"].map(h => (
                    <th key={h} className="text-left px-5 py-3.5 text-zinc-500 text-xs font-bold uppercase tracking-widest">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}
                    className="border-b border-white/4 hover:bg-white/2 transition-colors">

                    {/* Naam / E-mail */}
                    <td className="px-5 py-4">
                      <button data-testid={`cursist-naam-${u.id}`}
                        onClick={() => setProfielCursist({ id: u.id, naam: u.name, email: u.email })}
                        className="text-left hover:opacity-80 transition-opacity group">
                        <p className="text-white font-semibold text-sm group-hover:text-[#C9A84C] transition-colors flex items-center gap-1.5">
                          {u.name}
                          <Eye size={11} className="opacity-0 group-hover:opacity-60 transition-opacity text-zinc-500" />
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <p className="text-zinc-500 text-xs">{u.email}</p>
                          {u.plan === "schema" && (
                            <span className="text-xs bg-amber-500/15 text-amber-400 border border-amber-500/25 px-1.5 py-0.5 rounded-full font-semibold">Schema</span>
                          )}
                        </div>
                      </button>
                    </td>

                    {/* Toegangscode */}
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-[#C9A84C] font-mono font-bold text-sm tracking-widest">{u.access_code}</span>
                        <button onClick={() => { navigator.clipboard.writeText(u.access_code); showToast("Code gekopieerd"); }}
                          className="text-zinc-600 hover:text-[#C9A84C]"><Copy size={12} /></button>
                      </div>
                    </td>

                    {/* Schema's */}
                    <td className="px-5 py-4">
                      {(u.purchased_schemas || []).length === 0 ? (
                        <span className="text-zinc-600 text-xs italic">Geen</span>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {(u.purchased_schemas || []).map(s => <SchemaBadge key={s.product} schema={s} />)}
                        </div>
                      )}
                    </td>

                    {/* Aangemaakt */}
                    <td className="px-5 py-4 text-zinc-400 text-xs whitespace-nowrap">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString("nl-NL") : "–"}
                    </td>

                    {/* Schema Maker toggle */}
                    <td className="px-5 py-4">
                      <div className="flex flex-col gap-1.5 items-start">
                        <button onClick={() => handleSchemaBuilderToggle(u.id, u.schema_builder_unlocked)}
                          disabled={sbToggleLoading === u.id}
                          data-testid={`schema-builder-toggle-${u.id}`}
                          title={u.schema_builder_unlocked ? "Toegang intrekken" : "Toegang geven"}
                          className={`text-xs px-3 py-1.5 rounded-lg border font-bold transition-all flex items-center gap-1.5 ${
                            u.schema_builder_unlocked
                              ? "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                              : "border-zinc-700/50 text-zinc-500 hover:border-zinc-500/50 hover:text-zinc-300"
                          } disabled:opacity-40`}>
                          {sbToggleLoading === u.id
                            ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                            : u.schema_builder_unlocked ? "✓ Aan" : "Uit"
                          }
                        </button>
                        {u.schema_builder_unlocked && u.schema_builder_unlock_type && (
                          <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full ${
                            u.schema_builder_unlock_type === "betaald"
                              ? "bg-blue-500/10 text-blue-400"
                              : "bg-purple-500/10 text-purple-400"
                          }`}>
                            {u.schema_builder_unlock_type === "betaald" ? "Betaald" : "Handmatig"}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Vrije Training toggle */}
                    <td className="px-5 py-4">
                      <button onClick={() => handleVrijeTrainingToggle(u.id, u.vrije_training_enabled ?? true)}
                        disabled={vtToggleLoading === u.id}
                        data-testid={`vrije-training-toggle-${u.id}`}
                        title={u.vrije_training_enabled !== false ? "Toegang intrekken" : "Toegang geven"}
                        className={`text-xs px-3 py-1.5 rounded-lg border font-bold transition-all flex items-center gap-1.5 ${
                          u.vrije_training_enabled !== false
                            ? "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                            : "border-zinc-700/50 text-zinc-500 hover:border-zinc-500/50 hover:text-zinc-300"
                        } disabled:opacity-40`}>
                        {vtToggleLoading === u.id
                          ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                          : u.vrije_training_enabled !== false ? "✓ Aan" : "Uit"
                        }
                      </button>
                    </td>

                    {/* Rooster toggle */}
                    <td className="px-5 py-4">
                      <button onClick={() => handleRoosterToggle(u.id, u.persoonlijk_rooster_enabled ?? false)}
                        disabled={rtToggleLoading === u.id}
                        data-testid={`rooster-toggle-${u.id}`}
                        title={u.persoonlijk_rooster_enabled ? "Toegang intrekken" : "Toegang geven"}
                        className={`text-xs px-3 py-1.5 rounded-lg border font-bold transition-all flex items-center gap-1.5 ${
                          u.persoonlijk_rooster_enabled
                            ? "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                            : "border-zinc-700/50 text-zinc-500 hover:border-zinc-500/50 hover:text-zinc-300"
                        } disabled:opacity-40`}>
                        {rtToggleLoading === u.id
                          ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                          : u.persoonlijk_rooster_enabled ? "✓ Aan" : "Uit"
                        }
                      </button>
                    </td>

                    {/* Voortgang */}
                    <td className="px-5 py-4">
                      {u.plan === "schema" ? (
                        <span className="text-zinc-500 text-xs">Schema only</span>
                      ) : (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                            <BookOpen size={11} />
                            <MiniProgress done={u.lessons_done} total={20} />
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                            <ClipboardList size={11} />
                            <MiniProgress done={u.quizzes_done} total={4} color="#8b7536" />
                          </div>
                          {u.final_exam && (
                            <div className="flex items-center gap-1 text-xs text-[#C9A84C]">
                              <Trophy size={11} /> Certificaat ({u.final_exam}%)
                            </div>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Acties */}
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button data-testid={`cursist-schemas-btn-${u.id}`} onClick={() => onSchemaPanel(u)}
                          title="Schema's aanpassen"
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border font-bold transition-all border-[#C9A84C]/30 text-[#C9A84C] hover:bg-[#C9A84C]/10">
                          <BookOpen size={12} /> Schema's
                        </button>
                        <button onClick={() => handlePlanToggle(u.id, u.plan)}
                          data-testid={`plan-toggle-btn-${u.id}`}
                          title={u.plan === "schema" ? "Zet om naar Cursus" : "Zet om naar Schema"}
                          className={`text-xs px-2 py-1 rounded-lg border font-bold transition-all ${
                            u.plan === "schema"
                              ? "border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                              : "border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                          }`}>
                          {u.plan === "schema" ? "Schema" : "Cursus"}
                        </button>
                        <button onClick={() => handleResend(u.id)} disabled={resendLoading === u.id}
                          data-testid={`resend-btn-${u.id}`} title="Stuur toegangscode opnieuw"
                          className="p-1.5 text-zinc-500 hover:text-[#C9A84C] transition-colors disabled:opacity-50">
                          <RefreshCw size={14} className={resendLoading === u.id ? "animate-spin" : ""} />
                        </button>
                        <button onClick={() => handleResendWelcome(u.id)} disabled={welcomeMailLoading === u.id}
                          data-testid={`resend-welcome-btn-${u.id}`} title="Welkomstmail opnieuw sturen"
                          className="p-1.5 text-zinc-500 hover:text-emerald-400 transition-colors disabled:opacity-50">
                          <RefreshCw size={14} className={welcomeMailLoading === u.id ? "animate-pulse" : ""} />
                        </button>
                        <div className="relative">
                          <button onClick={() => onBericht(u)} data-testid={`trainer-bericht-btn-${u.id}`}
                            title={`Gesprek met ${u.name}`}
                            className={`p-1.5 transition-colors ${(unreadCounts[u.id] || 0) > 0 ? "text-[#C9A84C]" : (u.trainer_bericht ? "text-[#C9A84C]/60" : "text-zinc-500")} hover:text-[#C9A84C]`}>
                            <MessageSquare size={14} />
                          </button>
                          {(unreadCounts[u.id] || 0) > 0 && (
                            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-black flex items-center justify-center text-black"
                              style={{ background: "#ef4444" }}>
                              {unreadCounts[u.id]}
                            </span>
                          )}
                        </div>
                        <button onClick={() => setDeleteConfirm(u)} data-testid={`delete-btn-${u.id}`}
                          title="Verwijder cursist" className="p-1.5 text-zinc-600 hover:text-red-400 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-zinc-600 text-xs mt-3">{filtered.length} cursist{filtered.length !== 1 ? "en" : ""}</p>

      {/* Cursist Profiel Modal */}
      {profielCursist && (
        <SporterProfielModal
          sporter={profielCursist}
          token={token}
          onClose={() => setProfielCursist(null)}
          onStartTraining={() => {
            setProfielCursist(null);
            onGoToTrainModus?.();
          }}
        />
      )}

      {/* Verwijder bevestiging */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-7 max-w-sm w-full animate-fadeIn">
            <h3 className="text-white font-bold text-lg mb-2">Cursist verwijderen</h3>
            <p className="text-zinc-400 text-sm mb-6">
              Weet je zeker dat je <strong className="text-white">{deleteConfirm.name}</strong> wilt verwijderen?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="btn-course-outline flex-1 py-2.5 text-sm font-semibold">Annuleren</button>
              <button onClick={() => handleDelete(deleteConfirm.id)} data-testid="confirm-delete-btn"
                className="flex-1 py-2.5 bg-red-900/40 border border-red-500/30 text-red-400 font-semibold text-sm rounded-lg hover:bg-red-900/60 transition-colors">
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
